import { useSearchQueryFilter, useTrackEvents } from '@aibees/elephant/hooks';
import { Icon } from '@aibees/shared/components';
import { FilterInputError } from '@aibees/shared/components/number-range-input/components/filter-input-error';
import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo, useCallback } from 'react';
import { DurationFilterInput } from './components/duration-filter-input';
import { ErrorMessage } from './config';
import { useCurrentValue } from './hooks/use-current-value';
import { useGetDurationFilterError } from './hooks/use-get-duration-filter-error';
import * as SX from './sx';
import { LimitType, DurationFilterProps as Props, TimeType } from './types';
import { buildDraft } from './utils';

export const DurationFilter = memo(
  ({ className, field, name, nested, metadata, type }: Props) => {
    const { setParam, params } = useSearchQueryFilter({
      select: [field!],
    });
    const { track } = useTrackEvents();
    const currentValue = useCurrentValue(params, field!);
    const inputError = useGetDurationFilterError(currentValue);

    const handleChange = useCallback(
      (limit: LimitType, time: TimeType, value: string) => {
        const draft = buildDraft(currentValue, limit, time, value);
        setParam(field!, draft);
        track('FILTER', {
          filterName: field,
          filterType: type,
          filterValue: value,
          filterValues: draft,
          searchType: type,
        });
      },
      [currentValue, field, setParam, track, type]
    );

    return (
      <Card
        className={classnames('DurationFilter', className)}
        variant="filled"
        color="default"
        sx={SX.root}
      >
        {!nested && (
          <CardHeader
            title={
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={0.5}
              >
                <Typography variant="body3" color="grey.600" fontWeight={600}>
                  {name}
                </Typography>
                {metadata?.info && (
                  <Tooltip
                    title={metadata?.info}
                    arrow
                    placement="right"
                    maxWidth="unset"
                    enterDelay={200}
                  >
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Icon name="info" size={16} color="grey.400" />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            }
          />
        )}
        <CardContent>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            gap={0.5}
            marginBottom={inputError ? 0.5 : 0}
          >
            <Box display="flex" flexDirection="row" gap={1}>
              <Box display="flex" flex={2} alignItems="center">
                <Typography variant="body4" color="grey.500">
                  Years
                </Typography>
              </Box>
              <Box display="flex" paddingX={0.5} />
              <Box display="flex" flex={2} alignItems="center">
                <Typography variant="body4" color="grey.500">
                  Months
                </Typography>
              </Box>
            </Box>
            <Box display="flex" flexDirection="row" gap={0.5}>
              <Box
                display="flex"
                flex={1}
                justifyContent="center"
                alignItems="center"
              >
                <DurationFilterInput
                  placeholder="Min Year"
                  onChange={handleChange}
                  limit={LimitType.MIN}
                  time={TimeType.YEAR}
                  value={currentValue}
                  error={
                    !!inputError && !!currentValue[LimitType.MIN][TimeType.YEAR]
                  }
                />
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                color="grey.400"
              >
                -
              </Box>
              <Box
                display="flex"
                flex={1}
                justifyContent="center"
                alignItems="center"
              >
                <DurationFilterInput
                  placeholder="Min Month"
                  onChange={handleChange}
                  limit={LimitType.MIN}
                  time={TimeType.MONTH}
                  value={currentValue}
                  error={
                    !!inputError &&
                    !!currentValue[LimitType.MIN][TimeType.MONTH]
                  }
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="row" gap={0.5}>
              <Box
                display="flex"
                flex={1}
                justifyContent="center"
                alignItems="center"
              >
                <DurationFilterInput
                  placeholder="Max Year"
                  onChange={handleChange}
                  limit={LimitType.MAX}
                  time={TimeType.YEAR}
                  value={currentValue}
                  error={
                    !!inputError && !!currentValue[LimitType.MAX][TimeType.YEAR]
                  }
                />
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                color="grey.400"
              >
                -
              </Box>
              <Box
                display="flex"
                flex={1}
                justifyContent="center"
                alignItems="center"
              >
                <DurationFilterInput
                  placeholder="Max Month"
                  onChange={handleChange}
                  limit={LimitType.MAX}
                  time={TimeType.MONTH}
                  value={currentValue}
                  error={
                    !!inputError &&
                    !!currentValue[LimitType.MAX][TimeType.MONTH]
                  }
                />
              </Box>
            </Box>
          </Box>
          {!!inputError && (
            <FilterInputError message={ErrorMessage[inputError]} />
          )}
        </CardContent>
      </Card>
    );
  }
);

DurationFilter.displayName = 'DurationFilter';
